import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import * as clientErrorsService from '../../services/adminClientErrors.service';

/**
 * Real crash reports from real members, caught by ErrorBoundary — the
 * blind spot Jenn flagged 2026-09-02: "it's hard to get members to go to
 * the system at all, so reducing all friction or risk is a big deal."
 * Every one of today's real bugs only surfaced because someone happened
 * to say something; this is the alternative to relying on that.
 */
export default function AdminClientErrors({ embedded = false }) {
  const [errors, setErrors] = useState(null);
  const [showReviewed, setShowReviewed] = useState(false);

  const load = useCallback(() => {
    clientErrorsService.listRecent(showReviewed ? undefined : false).then(setErrors);
  }, [showReviewed]);

  useEffect(() => { load(); }, [load]);

  const markReviewed = async (id) => {
    await clientErrorsService.setReviewed(id, true);
    load();
  };

  const content = (
    <div className="max-w-4xl mx-auto">
      {!embedded && <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">Client Errors</h1>}
      <p className="text-sm text-gray-500 mb-4">Real crashes reported automatically from members' browsers — no report needed from them.</p>
      <label className="flex items-center gap-2 text-xs text-gray-500 mb-3">
        <input type="checkbox" checked={showReviewed} onChange={e => setShowReviewed(e.target.checked)} />
        Show reviewed too
      </label>
      {!errors ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : errors.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">Nothing to review — no crashes reported.</div>
      ) : (
        <div className="space-y-2">
          {errors.map(e => (
            <div key={e.id} className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-600 truncate">{e.message}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {e.route || '(unknown route)'} · {e.member_name || e.member_email || 'not signed in / unknown'} · {new Date(e.created_at).toLocaleString(undefined, { hour12: true })}
                  </p>
                </div>
                {!e.reviewed && (
                  <button onClick={() => markReviewed(e.id)} className="text-xs font-semibold text-reset-terracotta shrink-0">Mark reviewed</button>
                )}
              </div>
              {e.stack && (
                <pre className="mt-2 text-[11px] text-gray-500 bg-reset-cream rounded-lg p-2 overflow-x-auto whitespace-pre-wrap">{e.stack.slice(0, 800)}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) return content;
  return <DashboardLayout>{content}</DashboardLayout>;
}
